import React, { Suspense, useEffect, useState } from 'react';
import { Box, styled } from '@mui/material';
import { loadFederatedModule } from '../utils/loadFederatedModule';

declare global {
  interface Window {
    showCheckinsPanel?: (userId: string) => void;
    hideCheckinsPanel?: () => void;
  }
}

const CheckInsPanelContent = React.lazy(() => loadFederatedModule('SC/CheckInsPanelContent'));

const CheckInsPanelContainer = styled(Box)(() => ({
  position: 'fixed',
  top: 0,
  right: 0,
  width: '60vw',
  height: '100vh',
  backgroundColor: '#ffffff',
  borderLeft: '1px solid #e0e0e0',
  boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.1)',
  zIndex: 1150,
  overflow: 'hidden',
  '& *': {
    backgroundColor: 'transparent !important',
  },
  '& .MuiBox-root': {
    backgroundColor: 'transparent !important',
  },
  '& div[style*="background"]': {
    backgroundColor: '#ffffff !important',
  },
}));

const BackdropOverlay = styled(Box)(() => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 1149,
}));

const AdminModalHandler: React.FC = () => {
  const [showCheckinsPanel, setShowCheckinsPanel] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  const closeModal = () => {
    const angularModal = document.querySelector('.aside');
    const modalBackdrop = document.querySelector('.aside-backdrop');

    if (angularModal) {
      (angularModal as HTMLElement).style.display = '';
    }
    if (modalBackdrop) {
      (modalBackdrop as HTMLElement).style.display = '';
    }

    setShowCheckinsPanel(false);
    setUserId(null);
  };

  useEffect(() => {
    setShowCheckinsPanel(false);
    setUserId(null);

    window.showCheckinsPanel = (userIdParam: string) => {
      // Use the same HR Admin Super User check as the button display
      const isHrAdminSuperUser = (window as any).isHrAdminSuperUser;
      
      if (!isHrAdminSuperUser) {
        console.warn('Access denied: User is not HR Admin Super User');
        return;
      }

      const angularModal = document.querySelector('.aside');
      const modalBackdrop = document.querySelector('.aside-backdrop');

      if (angularModal) {
        (angularModal as HTMLElement).style.display = 'none';
      }
      if (modalBackdrop) {
        (modalBackdrop as HTMLElement).style.display = 'none';
      }

      setUserId(userIdParam);
      setShowCheckinsPanel(true);
    };

    window.hideCheckinsPanel = () => {
      const angularModal = document.querySelector('.aside');
      const modalBackdrop = document.querySelector('.aside-backdrop');

      if (angularModal) {
        (angularModal as HTMLElement).style.display = '';
      }
      if (modalBackdrop) {
        (modalBackdrop as HTMLElement).style.display = '';
      }

      setShowCheckinsPanel(false);
      setUserId(null);
    };

    return () => {
      // Cleanup window functions on unmount
      delete window.showCheckinsPanel;
      delete window.hideCheckinsPanel;
    };
  }, []);

  useEffect(() => {
    if (showCheckinsPanel) {
      document.body.style.overflow = 'hidden';

      // Hide Pendo elements by directly targeting DOM elements
      const pendoSelectors = [
        '._pendo-badge_',
      ];

      pendoSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          const htmlElement = element as HTMLElement;
          htmlElement.style.pointerEvents = 'none';
          htmlElement.style.visibility = 'hidden';
        });
      });
    } else {
      document.body.style.overflow = 'unset';
      // Show Pendo elements
      const pendoSelectors = [
        '._pendo-badge_',
      ];

      pendoSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          const htmlElement = element as HTMLElement;
          htmlElement.style.pointerEvents = '';
          htmlElement.style.visibility = '';
        });
      });
    }

    return () => {
      try {
        if (document.body) {
          document.body.style.overflow = 'unset';
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Modal cleanup error:', error);
      }
    };
  }, [showCheckinsPanel]);

  if (!showCheckinsPanel || !userId) {
    return null;
  }

  return (
    <>
      <BackdropOverlay onClick={closeModal} />
      <CheckInsPanelContainer>
        <div
          style={{
            minHeight: '300px',
            height: '100%',
            backgroundColor: '#ffffff',
            overflow: 'auto'
          }}
        >
          <Suspense fallback={null}>
            <CheckInsPanelContent
              userId={userId}
              onClose={closeModal}
              height="100%"
              adminMode
              style={{ backgroundColor: '#ffffff' }}
            />
          </Suspense>
        </div>
      </CheckInsPanelContainer>
    </>
  );
};

export default AdminModalHandler;
